import { Container, Heading, Text } from '@pcrm/fe/ui-kit';
import type { ReactNode } from 'react';

/**
 * Cardul de secțiune, portat din edinro (`features/settings/common/section.tsx`), care la
 * rândul lui urmează `Container`-ul din Medusa Admin.
 *
 * Ce face diferența vizuală, și de ce nu e o alegere de gust: **conținutul e împărțit de
 * linii subțiri, nu de chenare pe fiecare rând**. Cu chenar per rând, zece rânduri dau
 * zece cutii care se ceartă între ele; cu `divide-y` pe un singur container, dau o listă.
 */
export function SectionContainer({ children }: { children: ReactNode }) {
  return <Container className="divide-y p-0">{children}</Container>;
}

export function SectionHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-4 px-6 py-4">
      <div>
        <Heading level="h2">{title}</Heading>
        {subtitle ? (
          <Text className="text-ui-fg-subtle" size="small">
            {subtitle}
          </Text>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-x-2">{actions}</div> : null}
    </div>
  );
}

/**
 * Rând cheie–valoare, pe două coloane.
 *
 * `-` când valoarea lipsește, nu spațiu gol: altfel „câmp necompletat" și „câmp pe care
 * ecranul a uitat să-l randeze" arată identic.
 */
export function SectionRow({
  title,
  value,
  actions,
}: {
  title: ReactNode;
  value?: ReactNode;
  actions?: ReactNode;
}) {
  const isPlain = typeof value === 'string' || value === null || value === undefined;

  return (
    <div className="text-ui-fg-subtle grid w-full grid-cols-[1fr_1fr_auto] items-center gap-4 px-6 py-4">
      <Text size="small" weight="plus" leading="compact">
        {title}
      </Text>
      {isPlain ? (
        <Text size="small" leading="compact" className="whitespace-pre-line text-pretty">
          {value ?? '-'}
        </Text>
      ) : (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">{value}</div>
      )}
      <div className="flex items-center justify-end">{actions}</div>
    </div>
  );
}

/**
 * Starea unei secțiuni fără rânduri de arătat.
 *
 * Un singur loc pentru „se încarcă" / „a picat" / „nu e nimic", pentru că altfel fiecare
 * ecran le scrie cu alt spațiu și alt ton, iar diferența se vede.
 */
export function SectionEmpty({
  children,
  tone = 'muted',
}: {
  children: ReactNode;
  tone?: 'muted' | 'error';
}) {
  return (
    <div className="px-6 py-10 text-center">
      <Text size="small" className={tone === 'error' ? 'text-ui-fg-error' : 'text-ui-fg-subtle'}>
        {children}
      </Text>
    </div>
  );
}
